import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator, Firestore } from "firebase/firestore";
import { getAuth, connectAuthEmulator, Auth } from "firebase/auth";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { logger } from "./logger";

// Extend Window interface for debug access
declare global {
  interface Window {
    _firebaseDebug?: { db: Firestore; auth: Auth };
  }
}

// Configuración segura usando variables de entorno
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validación de configuración
const validateConfig = () => {
  const required = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN', 
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID'
  ];
  
  const missing = required.filter(key => !import.meta.env[key]);
  
  if (missing.length > 0) {
    throw new Error(
      `Firebase configuration incomplete. Missing: ${missing.join(', ')}\n` +
      `Please check your .env file. See .env.example for reference.`
    );
  }
};

validateConfig();

export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Debug SOLO en desarrollo y solo si está explícitamente habilitado
if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEBUG === 'true') {
  if (typeof window !== 'undefined') {
    window._firebaseDebug = { db, auth };
  }
}

// Emuladores solo en localhost
const shouldUseEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

if (shouldUseEmulators && isLocalhost) {
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectAuthEmulator(auth, "http://localhost:9099");
    connectFunctionsEmulator(functions, "localhost", 5001);
    connectStorageEmulator(storage, "localhost", 9199);
    logger.info('Firebase emulators connected successfully');
  } catch (error) {
    if ((error as Error)?.message?.includes('already connected')) {
      logger.debug('Firebase emulators already connected');
    } else {
      logger.warn('Failed to connect Firebase emulators - will use production', error);
    }
  }
}

export const isFirebaseConfigured = () => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
};